import React, { useContext, useEffect, useState } from 'react';
import { Route } from 'react-router';
import { Link, Switch, useHistory } from 'react-router-dom';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { VALUATIONS_GA_TAGS } from 'Utilities/analytics';
import { type HeroAnimationState } from 'Utilities/animation';
import { VRM_CHECK_CODES } from 'Utilities/vrm';

import { ComponentContent, CONTENT_SIZES } from 'Layout';

import { fireCookieConsentTakeover } from 'Components/CookieBanner/cookieConsent.constants';

import Lock from '../lock/Lock';
import TrustPilotImage from '../TrustPilotImage/TrustPilotImage';
import VrmInput from '../vrmInput/VrmInput';

import HomeVRMErrors from './homeVRMErrors/HomeVRMErrors';
import HomeVehicle from './HomeVehicle';
import { HomeVRMBereavement } from './HomeVRMBereavement';
import { HomeVRMBrand } from './HomeVRMBrand';
import LocalTexts from './HomeVRMTexts.json';

import styles from './HomeVRM.scss';

const LocalT = new MDText(LocalTexts);

export type HomeVRMProps = {
  analyticsEvents?: {
    onButtonClick?: (vrm: string) => void;
    onInputFocus?: () => void;
    onLandingPageInputFocus?: () => void;
  };
  inProgress?: boolean;
  loadingHandler?: (value: HeroAnimationState) => void;
  subTitle?: React.ReactNode;
  title?: React.ReactNode;
};

const mileageRegex = /\/mileage\/?$/;

const HomeVRM = ({
  analyticsEvents,
  inProgress,
  loadingHandler,
  subTitle = (
    <><span>8,000 dealers compete to give you</span> <span>their best price*. Find your highest offer!</span></>
  ),
  title = LocalT.translate('hero.home.title'),
}: HomeVRMProps) => {
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);

  const hasError = vehicle?.resultError;

  const history = useHistory();

  const [showMileage, setShowMileage] = useState(history.location.pathname.endsWith('/mileage'));

  useEffect(() => {
    // Fail safe to ensure you can't end up on mileage without a vehicle
    if (!vehicle?.id) {
      setShowMileage(false);
      history.replace({
        ...history.location,
        pathname: history.location.pathname.replace(mileageRegex, '') || '/',
      });
    }
    const unlisten = history.listen(({ pathname }) => {
      // If someone clicks `Not my car` it resets the store,
      // so need to make sure if no vehicle, no mileage page!
      setShowMileage(vehicle?.id ? pathname.endsWith('/mileage') : false);

      // reset url to '/' if `Not my car` is clicked and then browser back is clicked
      if (pathname.endsWith('/mileage') && !vehicle?.id) {
        history.replace({
          ...history.location,
          pathname: history.location.pathname.replace(mileageRegex, '') || '/',
        });
      }
    });

    return () => unlisten();
  }, [history, vehicle?.id]);

  useEffect(() => {
    // Fail safe to ensure you can't end up on mileage without a vehicle
    if (!vehicle?.id) {
      setShowMileage(false);
    }
  }, [vehicle?.id]);

  useEffect(() => {
    if (hasError) {
      VALUATIONS_GA_TAGS.VEHICLE_LOOKUP_FAILED(hasError);
    }
  }, [hasError]);

  const straplineCopy = (<ComponentContent maxWidth={ CONTENT_SIZES.large } paddings={ true }>
    <h2 className={ styles.heroStrap }>
      { subTitle }
    </h2>
  </ComponentContent>);

  const strapline = (showMileage)
    ? (
      <HomeVehicle {...{ vehicle }} />
    )
    : (
      <Switch>
        <Route exact component={HomeVRMBrand} path="/sell-my-car/brands/:brand" />
        <Route>
          <>
            <ComponentContent maxWidth={CONTENT_SIZES.big} paddings={true}>
              <Route component={HomeVRMBereavement} path='/sell-my-car/bereavement' />
              <h1 className={styles.heroTitle}>{title}</h1>
            </ComponentContent>
            { straplineCopy }
          </>
        </Route>
      </Switch>
    );

  const setMileageUrl = (value: boolean) => {
    let newPathname;
    const { pathname } = history.location;

    if (value) {
      newPathname = pathname === '/' ? '/mileage' : `${pathname}/mileage`;
      fireCookieConsentTakeover();
    } else {
      newPathname = pathname.replace(mileageRegex, '');
    }

    history.push(
      history.createHref({
        ...history.location,
        pathname: newPathname,
      }),
    );
  };

  const errorCodes: string[] = [
    VRM_CHECK_CODES.BAD_REQUEST,
    VRM_CHECK_CODES.EXCEEDED,
    VRM_CHECK_CODES.TOO_MANY_REQUESTS,
    VRM_CHECK_CODES.VEHICLE_GENERATION_FAILURE,
  ];

  return (
    <Lock lock={inProgress}>
      <ComponentContent
        className={cx(styles.component, { [styles.hasVehicle]: showMileage })}
        maxWidth={CONTENT_SIZES.full}
      >
        { (hasError) ? (
          <ComponentContent maxWidth={CONTENT_SIZES.large} paddings={false}>
            <HomeVRMErrors
              error={{
                error: vehicle?.resultError?.msg ?? '',
                vrm: vehicle?.resultError?.vrm ?? '',
              }}
            />
          </ComponentContent>
        ) : (
          <>{ strapline }</>
        )}
      </ComponentContent>
      {
        (!errorCodes.includes(hasError?.msg ?? '')) ? (
          <>
            <VrmInput
              setShowMileage={setMileageUrl}
              {...{
                analyticsEvents,
                inProgress,
                loadingHandler,
                showMileage,
                vehicle,
              }}
            />
            {
              ((!hasError && !showMileage)) ? (
                <ComponentContent
                  maxWidth={CONTENT_SIZES.large}
                >
                  <Link to="/reviews">
                    <TrustPilotImage />
                  </Link>
                </ComponentContent>
              ) : (null)
            }
          </>
        ) : (null)
      }
    </Lock>
  );
};

export default HomeVRM;
